import toast from 'react-hot-toast';

/**
 * Display a success toast notification
 * @param message - The message to display
 */
export function showSuccessToast(message: string): void {
  toast.success(message);
}

/**
 * Display an error toast notification
 * @param message - The error message to display
 * @param options - Optional configuration
 */
export function showErrorToast(message: string, options?: { duration?: number }): void {
  toast.error(message, {
    duration: options?.duration || 4000,
  });
}

/**
 * Display a loading toast notification
 * @param message - The loading message to display
 * @returns Toast ID that can be used to dismiss the toast
 */
export function showLoadingToast(message: string): string {
  return toast.loading(message);
}

/**
 * Dismiss a specific toast by ID
 * @param toastId - The ID of the toast to dismiss
 */
export function dismissToast(toastId: string): void {
  toast.dismiss(toastId);
}

/**
 * Display an error toast with a retry button
 * @param message - The error message to display
 * @param onRetry - Callback function to execute on retry
 */
export function showRetryToast(message: string, onRetry: () => void): void {
  toast.error(
    (t) => (
      <div className="flex flex-col gap-2">
        <p>{message}</p>
        <button
          onClick={() => {
            toast.dismiss(t.id);
            onRetry();
          }}
          className="px-3 py-1 bg-white text-gray-900 rounded hover:bg-gray-100 transition-colors text-sm font-medium"
        >
          Reintentar
        </button>
      </div>
    ),
    {
      duration: 10000,
    }
  );
}

/**
 * Display an info toast notification
 * @param message - The info message to display
 */
export function showInfoToast(message: string): void {
  toast(message, {
    icon: 'ℹ️',
  });
}

/**
 * Update an existing loading toast to success
 * @param toastId - The ID of the loading toast
 * @param message - The success message
 */
export function updateToastSuccess(toastId: string, message: string): void {
  toast.success(message, { id: toastId });
}

/**
 * Update an existing loading toast to error
 * @param toastId - The ID of the loading toast
 * @param message - The error message
 */
export function updateToastError(toastId: string, message: string): void {
  toast.error(message, { id: toastId });
}
